/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { Controller } from "react-hook-form";

import { Dropdown, IDropdownOption } from "office-ui-fabric-react/lib/Dropdown";
import { Label } from "office-ui-fabric-react/lib/Label";
import { TooltipHost } from "office-ui-fabric-react/lib/Tooltip";

import { Icon } from "@fluentui/react/lib/Icon";

import { FlexCol, FlexRow } from "Components/Common/Layout/Layout";
import { HookErrorMessage } from "Components/ReactHookForm/HookErrorMessage/HookErrorMessage";

import { IMultiDropdownControllerProps } from "./IMultiDropdownControllerProps";
import { dropdownStyles, getIconStyles } from "./MultiDropdownController.styles";

/**
 * MultiDropdownController.
 * @component
 * @param {IMultiDropdownControllerProps} props The component props.
 * @returns {React.ReactElement} MultiDropdownController
 */
export const MultiDropdownController = (
  props: IMultiDropdownControllerProps
): React.ReactElement => {
  const [count, setCount] = useState(0);
  const [dropdownOptions, setDropdownOptions] = useState(props.options);
  const [selectedItems, setSelectedItems] = useState<(string | number)[]>(
    props.defaultValues || []
  );

  useEffect(() => {
    setSelectedItems(props.defaultValues || []);
    setCount(props.defaultValues?.length || 0);
  }, []);

  useEffect(() => {
    if (!!props.maxLimit) {
      if (count >= props.maxLimit) {
        // where it's not the answer selected.
        dropdownOptions.forEach((option) => {
          if (!selectedItems.includes(option.key)) option.disabled = true;
        });
      } else {
        dropdownOptions.forEach((option) => (option.disabled = false));
      }
      setDropdownOptions((dropdownOptions) => [...dropdownOptions]);
    }
  }, [count]);

  /**
   *
   * @param {void} formOnChange React hook form onChange
   * @param {React.FormEvent<HTMLDivElement>} _event event when user selects/deselects
   * @param {IDropdownOption | undefined} item item clicked
   * @param {(string | number)[]} values values selected
   * @returns {void} onchange for multi select
   */
  const onChange = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formOnChange: (...event: any[]) => void,
    _event: React.FormEvent<HTMLDivElement>,
    item: IDropdownOption | undefined,
    values: (string | number)[]
  ): void => {
    const newSelectedItems = [...values];
    if (!!item) {
      const currIndex = newSelectedItems.indexOf(item.key);
      if (item.selected) {
        !!props.onItemSelected && props.onItemSelected(item, currIndex);
        // add the option if it's checked
        newSelectedItems.push(item.key);
      } else {
        // remove the option if it's unchecked
        if (currIndex > -1) {
          !!props.onItemRemoved && props.onItemRemoved(item, currIndex);
          newSelectedItems.splice(currIndex, 1);
        }
      }
    }
    setSelectedItems(newSelectedItems);
    setCount(newSelectedItems.length);
    formOnChange(newSelectedItems);
  };

  return (
    <FlexCol>
      <FlexRow>
        <Label htmlFor={props.inputName} required={props.isRequired} styles={props.labelStyles}>
          {props.labelDisplayName}
        </Label>
        {props.hasTooltip && (
          <TooltipHost
            id={props.inputName}
            content={props.toolTipDisplayText || props.labelDisplayName}
          >
            <Icon
              aria-label="information"
              iconName="info"
              role="img"
              styles={getIconStyles(!!props.isRequired)}
              tabIndex={0}
            />
          </TooltipHost>
        )}
      </FlexRow>
      <FlexRow>
        <Controller
          name={props.inputName}
          control={props.control}
          render={({ field }): JSX.Element => (
            <Dropdown
              styles={dropdownStyles}
              ariaLabel={props.labelDisplayName}
              selectedKeys={selectedItems as string[] | number[]}
              onChange={(e, option): void => {
                onChange(field.onChange, e, option, selectedItems);
              }}
              options={dropdownOptions}
              disabled={props.isDisabled}
              multiSelect={true}
              placeholder={props.placeholderText}
            />
          )}
        />
      </FlexRow>
      <HookErrorMessage errors={props.errors} inputName={props.inputName} />
    </FlexCol>
  );
};
